<template>
  <div class="c-header-nav-item d-md-down-none mx-2">
    <router-link
      class="notif-link"
      :to="notificationTarget"
    >
      <div class="notif-icon-wrap">
        <CIcon name="cil-bell"/>
        <span v-if="unreadCount" class="notif-badge">{{ unreadCount }}</span>
      </div>
    </router-link>
  </div>
</template>

<script>
import { canAutoRefresh } from '@/projects/utils/refresh-guard'
import {
  detectRouteRole,
  getActiveWorkspaceRole,
  getAssignedRoles,
  getWorkspaceRole,
  workspaceRoleEventName
} from '@/utils/role-dashboard'
import {
  buildWorkflowNotificationsForRoles,
  dedupeWorkflowNotifications,
  listWorkflowNotificationsForRoles,
  mergeStoredWorkflowNotificationState,
  workflowNotificationEventName
} from '@/utils/workflow-notifications'

const NOTIFICATION_ROLES = ['admin', 'committee', 'user']
const NOTIFICATION_POLL_INTERVAL_MS = 30000
const NOTIFICATION_CACHE_MAX_AGE_MS = 30000

export default {
  name: 'TheHeaderDropdownNotif',
  data() {
    return {
      unreadCount: 0,
      lastRefreshAt: 0,
      refreshPromise: null,
      workspaceRoleVersion: 0,
      pollTimer: null
    }
  },
  computed: {
    notificationTarget() {
      const roles = this.resolveCurrentRoles()
      if (roles.length > 1) {
        return { path: '/notifications', query: { workspace: 'all' } }
      }
      return roles[0]
        ? { path: '/notifications', query: { workspace: roles[0] } }
        : '/notifications'
    }
  },
  watch: {
    '$route.fullPath'() {
      this.handleWorkflowNotificationChange()
    }
  },
  mounted() {
    this.refreshUnreadCount({ forceRefresh: true })
    this.pollTimer = window.setInterval(() => {
      if (canAutoRefresh()) this.refreshUnreadCount({ forceRefresh: true })
    }, NOTIFICATION_POLL_INTERVAL_MS)
    window.addEventListener('focus', this.refreshUnreadCount)
    window.addEventListener('storage', this.refreshUnreadCount)
    window.addEventListener(workflowNotificationEventName(), this.handleWorkflowNotificationChange)
    window.addEventListener(workspaceRoleEventName(), this.handleWorkspaceRoleChange)
  },
  beforeDestroy() {
    if (this.pollTimer) {
      window.clearInterval(this.pollTimer)
      this.pollTimer = null
    }
    window.removeEventListener('focus', this.refreshUnreadCount)
    window.removeEventListener('storage', this.refreshUnreadCount)
    window.removeEventListener(workflowNotificationEventName(), this.handleWorkflowNotificationChange)
    window.removeEventListener(workspaceRoleEventName(), this.handleWorkspaceRoleChange)
  },
  methods: {
    assignedNotificationRoles() {
      const roles = getAssignedRoles(this.$store).filter(role => NOTIFICATION_ROLES.includes(role))
      return roles.length ? roles : ['user']
    },
    resolveCurrentRoles() {
      this.workspaceRoleVersion
      const assignedRoles = this.assignedNotificationRoles()
      const workspaceQuery = String(this.$route && this.$route.query && this.$route.query.workspace || '').trim().toLowerCase()
      if (workspaceQuery === 'all' || workspaceQuery === 'multiuser') {
        return assignedRoles
      }
      if (workspaceQuery) {
        const routeRole = detectRouteRole(this.$store, this.$route)
        if (routeRole && assignedRoles.includes(routeRole)) return [routeRole]
      }

      const activeRole = getActiveWorkspaceRole(this.$store)
      if (activeRole && assignedRoles.includes(activeRole)) return [activeRole]

      const workspaceRole = getWorkspaceRole(this.$store)
      if (workspaceRole === 'multiuser') {
        return assignedRoles
      }

      const routeRole = detectRouteRole(this.$store, this.$route)
      const selectedRole = [routeRole, workspaceRole].find(role => assignedRoles.includes(role))
      return [selectedRole || assignedRoles[0]]
    },
    isUnread(item) {
      return !(item && item.read)
    },
    handleWorkspaceRoleChange() {
      this.workspaceRoleVersion += 1
      this.handleWorkflowNotificationChange()
    },
    handleWorkflowNotificationChange() {
      this.lastRefreshAt = 0
      this.refreshUnreadCount({ forceRefresh: true })
    },
    async refreshUnreadCount(options = {}) {
      const currentRoles = this.resolveCurrentRoles()
      const storedItems = listWorkflowNotificationsForRoles(currentRoles, { includeDismissed: true })

      const cacheMaxAge = NOTIFICATION_CACHE_MAX_AGE_MS
      if (this.refreshPromise) return this.refreshPromise
      if (!options.forceRefresh && this.lastRefreshAt && (Date.now() - this.lastRefreshAt) < cacheMaxAge) return

      this.refreshPromise = (async () => {
        const queueItems = await buildWorkflowNotificationsForRoles(currentRoles, {
          cacheMaxAge,
          forceRefresh: !!options.forceRefresh
        })
        const mergedQueueItems = mergeStoredWorkflowNotificationState(queueItems, storedItems)
        const deduped = dedupeWorkflowNotifications(mergedQueueItems)
        this.unreadCount = deduped.filter(item => this.isUnread(item)).length
        this.lastRefreshAt = Date.now()
      })()

      try {
        await this.refreshPromise
      } finally {
        this.refreshPromise = null
      }
    }
  }
}
</script>

<style scoped>
.notif-link {
  align-items: center;
  color: inherit;
  cursor: pointer;
  display: flex;
}

.notif-icon-wrap {
  align-items: center;
  background: linear-gradient(135deg, #fff4e8 0%, #fff 100%);
  border: 1px solid #eadacc;
  border-radius: 999px;
  display: inline-flex;
  min-height: 38px;
  padding: 0 12px;
  position: relative;
}

.notif-badge {
  align-items: center;
  background: #c53030;
  border: 2px solid #fff;
  border-radius: 999px;
  color: #fff;
  display: inline-flex;
  font-size: 11px;
  font-weight: 700;
  height: 20px;
  justify-content: center;
  min-width: 20px;
  padding: 0 6px;
  position: absolute;
  right: -4px;
  top: -6px;
}

.c-icon {
  color: #7e1517;
  margin-right: 0;
}
</style>
